import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Chart from 'chart.js/auto';
import AddToRadar from './AddToRadar';
import Header from './Header';
import { capitalizeFirstLetterOfEachWord, formatPrice, saveToRecentlyViewed, 
    getRecentlyViewed, 
    handleProductClickAmazon} from '../utils';
import ProductBox from './ProductBox';
import Footer from './Footer';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import BackButton from './BackButton';
import Searchbar from './Searchbar';
import YoutubeReviews from './YoutubeReviews';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { toast } from 'react-hot-toast';
import { FaShoppingCart, FaTrashAlt } from 'react-icons/fa';
import ShareOrCopyButton from './ShareOrCopyButton';
// import ProductCompare from './ProductCompare';

const ProductPage = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [similarProducts, setSimilarProducts] = useState([]);
    const [priceComparizons, setPriceComparizons] = useState([]);
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const [selectedRange, setSelectedRange] = useState('6m'); // '1w', '1m', or '3m'
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const { cartItems, addToCart, removeFromCart } = useCart();
    const [inCart, setInCart] = useState(false);
    const [priceStats, setPriceStats] = useState({
        lowest: null,
        highest: null,
        average: null
    });
    

useEffect(() => {
  if (product) {
    setInCart(cartItems.some(item => item._id === product._id));
  }
}, [cartItems, product]);


    const affiliateId = 'glamalertz0b-21'; // Replace with your actual tracking ID

    const getAffiliateLink = (link, store) => {
        const url = new URL(link);
        
        if (store === 'amazon') {
            const asinMatch = url.pathname.match(/\/dp\/([A-Z0-9]{10})/);
            
            if (asinMatch && asinMatch[1]) {
                const asin = asinMatch[1];
                const simplifiedLink = `https://www.amazon.sa/dp/${asin}/ref=nosim?tag=${affiliateId}`;
                return simplifiedLink;
            }
        }
        
        return link;
    };

    const fetchProductDetails = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/main/product/${id}`);
            setProduct(response.data);
        } catch (error) {
            console.error("Error fetching product details:", error);
        }
    };

    const fetchPriceComparizon = async (searchTerm) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/main/price-compare`, {
                params: { searchTerm }
            });
            const fetchedProducts = response.data.stores;
            
            const uniqueProducts = [];
            const productIds = new Set();
        
            for (const product of fetchedProducts) {
                if (!productIds.has(product._id)) {
                    uniqueProducts.push(product);
                    productIds.add(product._id);
                }
            }
        
            setPriceComparizons(uniqueProducts);
        } catch (error) {
            console.error("Error fetching similar products:", error);
        }
    };

    const fetchSimilarProducts = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/main/similar/${id}`);
            const fetchedProducts = response.data;
    
            const uniqueProducts = [];
            const productIds = new Set();
    
            for (const product of fetchedProducts) {
                if (!productIds.has(product._id)) {
                    uniqueProducts.push(product);
                    productIds.add(product._id);
                }
            }
    
            setSimilarProducts(uniqueProducts);
        } catch (error) {
            console.error("Error fetching similar products:", error);
        }
    };

    const fetchPriceHistory = async (range) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/product/history/${id}`, {
                params: { range }
            });
    
            // Step 1: Normalize prices from the API
            const priceHistory = response.data.map(item => ({
                ...item,
                price: parseFloat(item.price.match(/\d+(\.\d+)?/)[0])
            }));
    
            // ✅ Step 2: Apply smoothing
            const SMOOTH_THRESHOLD = 0.5;
            const smoothedData = [];
            let lastAcceptedPrice = null;
    
            for (const entry of priceHistory) {
                const price = parseFloat(entry.price.toFixed(1)); // round to 1 decimal
                if (
                    lastAcceptedPrice === null ||
                    Math.abs(price - lastAcceptedPrice) >= SMOOTH_THRESHOLD
                ) {
                    smoothedData.push({ ...entry, price });
                    lastAcceptedPrice = price;
                }
            }
    
            // Step 3: Calculate stats based on smoothed data
            const prices = smoothedData.map(item => item.price);
            const lowestPrice = Math.min(...prices);
            const highestPrice = Math.max(...prices);
            const averagePrice = (prices.reduce((acc, price) => acc + price, 0) / prices.length).toFixed(2);
    
            setPriceStats({
                lowest: lowestPrice,
                highest: highestPrice,
                average: averagePrice
            });
    
            // Step 4: Prepare chart data
            const labels = smoothedData.map(entry =>
                new Date(entry.timestamp).toLocaleDateString('en-GB')
            );
            const data = smoothedData.map(entry => entry.price);
    
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
    
            if (chartRef.current) {
                const ctx = chartRef.current.getContext('2d');
                const gradient = ctx.createLinearGradient(0, 0, 0, 400);
                gradient.addColorStop(0, 'rgba(43, 90, 121, 0.4)');
                gradient.addColorStop(1, 'rgba(54, 162, 235, 0)');
    
                chartInstanceRef.current = new Chart(ctx, {
                    type: 'line',
                    data: {
                        labels,
                        datasets: [{
                            label: 'Price (SAR)',
                            data,
                            borderColor: 'rgb(54, 162, 235)',
                            backgroundColor: gradient,
                            fill: true,
                            tension: 0.4,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                            pointBackgroundColor: 'rgb(54, 162, 235)',
                            pointBorderColor: '#fff',
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                title: {
                                    display: true,
                                    text: 'Date',
                                    color: '#333',
                                    font: { size: 12 }
                                },
                                grid: {
                                    color: '#f0f0f0'
                                },
                                ticks: {
                                    color: '#333',
                                    font: { family: 'Inter', size: 12 }
                                }
                            },
                            y: {
                                title: {
                                    display: true,
                                    text: 'Price (SAR)',
                                    color: '#333',
                                    font: { size: 12 }
                                },
                                grid: {
                                    color: '#f0f0f0'
                                },
                                ticks: {
                                    color: '#333',
                                    font: { family: 'Inter', size: 12 }
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                display: false
                            },
                            tooltip: {
                                backgroundColor: '#333',
                                titleFont: {
                                    size: 14,
                                    weight: 'bold'
                                },
                                bodyFont: {
                                    size: 12
                                },
                                padding: 10,
                                cornerRadius: 4
                            }
                        }
                    }
                });
            }
        } catch (error) {
            console.error("Error fetching price history:", error);
        }
    };
    

    // const fetchPriceHistory = async (range) => {
    //     try {
    //         const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/product/history/${id}`, {
    //             params: { range }
    //         });
    
    //         const priceHistory = response.data.map(item => ({
    //             ...item,
    //             price: parseFloat(item.price.match(/\d+(\.\d+)?/)[0])
    //         }));

    //              // Calculate price stats
    //     const prices = priceHistory.map(item => item.price);
    //     const lowestPrice = Math.min(...prices);
    //     const highestPrice = Math.max(...prices);
    //     const averagePrice = (prices.reduce((acc, price) => acc + price, 0) / prices.length).toFixed(2);

    //     setPriceStats({
    //         lowest: lowestPrice,
    //         highest: highestPrice,
    //         average: averagePrice
    //     });

    
    //         const labels = priceHistory.map(entry =>
    //             new Date(entry.timestamp).toLocaleDateString('en-GB')
    //         );
    //         const data = priceHistory.map(entry => entry.price);
    
    //         if (chartInstanceRef.current) {
    //             chartInstanceRef.current.destroy();
    //         }
    
    //         if (chartRef.current) {
    //             const ctx = chartRef.current.getContext('2d');
    
    //             // Create gradient fill
    //             // const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    //             // gradient.addColorStop(0, 'rgba(75, 192, 192, 0.3)');
    //             // gradient.addColorStop(1, 'rgba(75, 192, 192, 0)');

    //             const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    //             gradient.addColorStop(0, 'rgba(43, 90, 121, 0.4)');
    //             gradient.addColorStop(1, 'rgba(54, 162, 235, 0)');
    
    //             chartInstanceRef.current = new Chart(ctx, {
    //                 type: 'line',
    //                 data: {
    //                     labels,
    //                     datasets: [{
    //                       label: 'Price (SAR)',
    //                       data,
    //                       borderColor: 'rgb(54, 162, 235)',
    //                     //   borderColor: 'rgb(62,43,205)',
    //                       backgroundColor: gradient,
    //                       fill: true,
    //                       tension: 0.4,
    //                       pointRadius: 4,
    //                       pointHoverRadius: 6,
    //                       pointBackgroundColor: 'rgb(54, 162, 235)',
    //                     //   pointBackgroundColor: '#FF9900',
    //                       pointBorderColor: '#fff',
    //                     }]
    //                   },
    //                 options: {
    //                     responsive: true,
    //                     maintainAspectRatio: false,
    //                     scales: {
    //                         x: {
    //                             title: {
    //                                 display: true,
    //                                 text: 'Date',
    //                                 color: '#333',
    //                                 font: { size: 12 }
    //                             },
    //                             grid: {
    //                                 color: '#f0f0f0'
    //                             },
    //                             ticks: {
    //                                 color: '#333',
    //                                 font: { family: 'Inter', size: 12 }
    //                             }
    //                         },
    //                         y: {
    //                             title: {
    //                                 display: true,
    //                                 text: 'Price (SAR)',
    //                                 color: '#333',
    //                                 font: { size: 12 }
    //                             },
    //                             grid: {
    //                                 color: '#f0f0f0'
    //                             },
    //                             ticks: {
    //                                 color: '#333',
    //                                 font: { family: 'Inter', size: 12 }
    //                             }
    //                         }
    //                     },
    //                     plugins: {
    //                         legend: {
    //                             display: false
    //                         },
    //                         tooltip: {
    //                             backgroundColor: '#333',
    //                             titleFont: {
    //                                 size: 14,
    //                                 weight: 'bold'
    //                             },
    //                             bodyFont: {
    //                                 size: 12
    //                             },
    //                             padding: 10,
    //                             cornerRadius: 4
    //                         }
    //                     }
    //                 }
    //             });
    //         }
    //     } catch (error) {
    //         console.error("Error fetching price history:", error);
    //     }
    // };

    const recordUserClick = async (item, type) => {
        try {
          await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/main/user/click`, {
            itemId: item._id,
            type,
            name: item.name,
            arabicName: item.arabicName,
            link: item.link,
            store: item.store,
            language: i18n.language
          }, 
        //   { withCredentials: true }
        );
        } catch (err) {
          console.error('Failed to record click:', err);
        }
      };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        fetchProductDetails();
        fetchSimilarProducts();
        saveToRecentlyViewed(product);
        const history = getRecentlyViewed();
        setRecentlyViewed(history);
    }, [id]);

    // useEffect(() => {
    //     if (product) {
    //         fetchPriceComparizon(product.name);
    //         fetchPriceHistory();
    //         scrollToTop();
    //     }
    // }, [product]);

    useEffect(() => {
        if (product) {
            // fetchPriceComparizon(product.name);
            // fetchPriceHistory();s
            setSelectedRange('6m');
            fetchPriceHistory('6m');
            scrollToTop();
        }
    }, [product]);
    
    useEffect(() => {
        if (product) {
            fetchPriceHistory(selectedRange); // Pass selectedRange here
        }
    }, [selectedRange]);
    

    const slidesToShowLarge = (similarProducts.length < 3) ? similarProducts.length : 3;
    const slidesToShowMedium = (similarProducts.length < 2) ? similarProducts.length : 2;

    const settings = {
        dots: true,
        infinite: false,
        speed: 500,
        slidesToShow: slidesToShowLarge,
        slidesToScroll: 1,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: slidesToShowMedium,
                    slidesToScroll: 1
                }
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1
                }
            }
        ]
    };

    return (
        <div className='bg-white'>
            {product ? (
                <>
                    <Helmet>
                        <title>{`${capitalizeFirstLetterOfEachWord(product.name)} | GlamAlertz - Best Deals, Offers & Price History`}</title>
                        <meta name="description" content={`Discover the latest prices, discounts, and deals for ${product.name} at GlamAlertz. Compare prices across multiple stores and find the best offers today! Get price history.`} />
                        <meta property="og:title" content={`${capitalizeFirstLetterOfEachWord(product.name)} | GlamAlertz - Best Deals & Price History`} />
                        <meta property="og:description" content={`Explore ${product.name}'s price history and compare offers from Amazon and other stores. Shop smart with GlamAlertz.`} />
                        <meta property="og:image" content={product.image || "/logo2.png"} />
                        <meta property="og:url" content={`https://www.glamalertz.com/product/${id}`} />
                    </Helmet>

                    <Header />
                        <Searchbar 
                                placeholder={t('searchbar.landingPage')} 
                                onSearchSubmit={(value) => {
                                    // handle the actual search logic here
                                    console.log('User searched:', value);
                                    // maybe call your API or filter state, etc.
                                  }}
                                />
                    <BackButton />
                    <div className="mx-auto py-4 px-6">
                        <div className="flex flex-col sm:flex-row">
                            <div className="flex-1">
                                <ShareOrCopyButton />
                                {/* <img className="w-full max-w-xs mx-auto" src={product.image || "/logo2.png"} alt={`Image of ${product.name}`} onError={(e) => e.target.src = '/logo2.png'} /> */}
                                <img
                                    className="w-full max-w-xs max-h-64 object-contain mx-auto"
                                    src={product.image || "/logo2.png"}
                                    alt={`Image of ${product.name}`}
                                    onError={(e) => (e.target.src = "/logo2.png")}
                                    />
                                <img className="mt-2 w-20 mx-auto" src={product.storeLogo} alt={`Logo of ${product.store}`} onError={(e) => e.target.src = '/logo2.png'} />
                            </div>
                            <div className="flex-1 ml-4">
                                <h1 className="text-xl font-bold mb-2">
                                {i18n.language === 'ar' && product.arabicName
                                    ? `${product.arabicName} ${t('productPage.productName')}`
                                    : `${capitalizeFirstLetterOfEachWord(product.name)} ${t('productPage.productName')}`}
                                </h1>


                                {/* <div className="text-lg">
                                    {product.prices.discounted ? (
                                        <span className="text-xl text-red-600">{`${t('priceBox.now')}  ${formatPrice(product.prices.discounted)} ${product.currency}`}</span>
                                    ) : (
                                        <span className="line-through text-gray-600">{`${t('priceBox.now')}  ${formatPrice(product.prices.original)} ${product.currency}`}</span>
                                    )}
                                </div> */}
                                {product.prices.discounted &&
                                 product.prices.discounted !== product.prices.original ? (
                                  <div>
                                    <span className="text-primary font-semibold text-sm discounted-price-productBox">
                                      {`${t('priceBox.now')} ${formatPrice(product.prices.discounted)} ${product.currency}`}
                                    </span>
                                    <span className="line-through text-xs text-gray-400 ml-2 original-price-productBox">
                                      {formatPrice(product.prices.original)} {product.currency}
                                    </span>
                                    {product.discountPercentage && (
                                      <div className="badge badge-error badge-sm mt-1">
                                        {product.discountPercentage}% OFF
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-primary font-semibold text-sm original-price-productBox">
                                    {`${t('priceBox.now')} ${formatPrice(product.prices.discounted || product.prices.original)} ${product.currency}`}
                                  </span>
                                )}
                            </div>
                        </div>


                        {/* <ProductCompare product={product} /> */}

                <div className="product-actions flex-col justify-start items-center mt-4 md:w-[20%] sm:w-[50%]">
                <a 
                    href={getAffiliateLink(product.link, product.store)} 
                    className="btn btn-primary text-white bg-theme-primary hover:bg-theme-primary-dark px-6 py-3 rounded-xl w-full transition-all duration-200"
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => {
                        recordUserClick(product, 'buyNow');
                    }}
                >
                    {`${t('productPage.buyNow')} ${product.store}!`}
                </a>

                <button
                onClick={() => {
                    if (inCart) {
                    removeFromCart(product._id);
                    toast.success('Removed from cart');
                    } else {
                    addToCart({ ...product, quantity: 1 });
                    toast.success('Added to cart');
                    }
                    setInCart(!inCart);
                }}
                className={`mb-2 rounded-xl btn ${inCart ? 'bg-red-500' : 'bg-green-700'} text-white w-full mt-4`}
                >
                <FaShoppingCart className="text-xl" />
                {inCart ? 'Remove from Cart' : 'Add to Cart'}
                </button>
            
                <AddToRadar key={product._id} product={product} />
                </div>

                               {/* Price History Chart */}
                        <div className="my-8 w-full lg:pl-[5%] lg:pr-[5%]">
                        <h2 className="text-lg font-semibold mb-0 text-left rtl:text-right text-[rgb(62,43,205)] pt-4">{t('productPage.priceHistory')}</h2>
                        <p className='text-md text-gray-600'>{t('productPage.priceHistory.description')}</p>

                                        {/* price stats */}
                <div className="price-stats mt-4 flex flex-col md:flex-row">
                {priceStats.lowest && (
                    <div className="stat">
                        <h4 className='text-primary'>{t('productPage.priceStats.lowestPrice')}</h4>
                        <p>{formatPrice(priceStats.lowest)} {product.currency}</p>
                    </div>
                )}
                {priceStats.highest && (
                    <div className="stat">
                        <h4 className='text-primary'>{t('productPage.priceStats.highestPrice')}</h4>
                        <p>{formatPrice(priceStats.highest)} {product.currency}</p>
                    </div>
                )}
                {priceStats.average && (
                    <div className="stat">
                        <h4 className='text-primary'>{t('productPage.priceStats.averagePrice')}</h4>
                        <p>{formatPrice(priceStats.average)} {product.currency}</p>
                    </div>
                )}
        </div>

                        <div role="tablist" className="tabs tabs-md tabs-bordered mb-4 pt-4 justify-center">
                            <a role="tab" className={`tab ${selectedRange === '1m' ? 'tab-active dark:text-primary dark:hover:text-primary' : 'dark:!text-gray-400 dark:hover:!text-black'}`} onClick={() => setSelectedRange('1m')}>{t('productPage.priceHistory.1month')}</a>
                            <a role="tab" className={`tab ${selectedRange === '3m' ? 'tab-active dark:text-primary dark:hover:text-primary' : 'dark:!text-gray-400 dark:hover:!text-black'}`} onClick={() => setSelectedRange('3m')}>{t('productPage.priceHistory.3months')}</a>
                            <a role="tab" className={`tab ${selectedRange === '6m' ? 'tab-active dark:text-primary dark:hover:text-primary' : 'dark:!text-gray-400 dark:hover:!text-black'}`} onClick={() => setSelectedRange('6m')}>{t('productPage.priceHistory.6months')}</a>
                            <a role="tab" className={`tab ${selectedRange === 'all' ? 'tab-active dark:text-primary dark:hover:text-primary' : 'dark:!text-gray-400 dark:hover:!text-black'}`} onClick={() => setSelectedRange('all')}>{t('productPage.priceHistory.all')}</a>
                        </div>
                        <div className="h-64" dir="ltr">
                            <canvas ref={chartRef} />
                        </div>
                    </div>



                    <div className="overflow-x-auto flex flex-col space-x-4 py-4 snap-x snap-mandatory lg:pl-[5%] lg:pr-[5%] lg:pb-[5%]">
  {recentlyViewed && recentlyViewed.length > 0 && (
    <>
      <div className="flex flex-col">
        <h3 className="text-lg font-semibold text-left rtl:text-right text-[rgb(62,43,205)]">
          {t('productPage.recentlyViewed')}
        </h3>
        <p className="text-md pb-4 text-gray-600">
          {t('productPage.recentlyViewed.description')}
        </p>
      </div>

      <div className="flex">
        {recentlyViewed.map((product) => (
          <Link
            to={`/product-page/${product._id}`}
            key={product._id}
            className="min-w-[160px] max-w-[160px] snap-start"
            onClick={() => handleProductClickAmazon(product)}
          >
            <img
              src={product.image}
              className="rounded-lg h-24 object-contain w-full"
              alt={product.name}
            />
            <div className="text-sm truncate mt-1">
            {i18n.language === 'ar' && product.arabicName
                ? product.arabicName
                : product.name}
            </div>

          </Link>
        ))}
      </div>
    </>
  )}
</div>

    {/* Similar Products Carousel */}
    { similarProducts.length ? 
<div className="pt-4 product-availability lg:pl-[5%]">
    <h3 className="text-lg font-semibold text-left rtl:text-right text-[rgb(62,43,205)]">{t('productPage.similarItems')}</h3>
    <p className="text-md pb-4 text-gray-600">{t('productPage.similarItems.description')}</p>
    <div className="carousel carousel-center space-x-4 p-4 overflow-x-auto w-full">
    {similarProducts.map(product => (
        <div key={product._id} className="carousel-item px-4 flex justify-center items-stretch">
        <ProductBox product={product} />
    </div>
    
    ))}
    </div>
</div> : null }
{/* <YoutubeReviews productName={product.name} /> */}
{/* {product && <YoutubeReviews productName={product.name} />} */}


</div>
</>
) : (
<div className="text-center py-4">{t('general.loading')}</div>
)}
<Footer />
</div>
    );
};

export default ProductPage;